// Table Component
import Table from '../ui/table/Table'

// Components
import ComponentInvlevelInList from './ComponentInvlevelInList'

// Dayjs
import dayjs from 'dayjs'

// Next
import { Link, useLocation } from 'react-router-dom'

// Supabase
import { useApiSWRReference } from '@/hooks/useApiSWR'

// Classnames
import classNames from 'classnames'

// Icons
import {
    IconAlertCircleFilled,
    IconCheck,
    IconClockCog,
    IconTransferIn,
    IconX,
} from '@tabler/icons-react'

// UI
import SKU from '../ui/SKU'
import Badge from '../ui/Badge'
import ComponentInventoryStatus from './ComponentInventoryStatus'

export default function ComponentsTable() {
    // Cells
    const SKUCell = (item) => (
        <div className="table-cell w-[100px] p-5 text-gray-600">
            <SKU component={item} size="sm" />
        </div>
    )

    const NameCall = (item) => {
        const location = useLocation()
        const from = btoa(location.pathname + location.search)
        const fullName = [item.name_fr, item.name].filter(Boolean).join(' — ')

        return (
            <Link
                to={`/components/${item.id}?from=${from}`}
                className="table-cell p-5 hover:bg-gray-100 relative"
                key={item.id}
            >
                <div className="whitespace-nowrap 2xl:flex 2xl:items-center 2xl:gap-2">
                    <span className="block text-sm leading-5 text-baltic">
                        {fullName}
                    </span>
                    <div className="mt-1 flex items-center gap-2 2xl:order-first 2xl:mt-0">
                        <SKU component={item} size="xs" />
                        {item?.is_archived && <Badge color="gray" size="xs">Archivé</Badge>}
                    </div>
                </div>
            </Link>
        )
    }

    const StockCell = (item) => (
        <div className="table-cell h-full hover:bg-gray-100 relative">
            <div className="absolute inset-0 flex">
                <ComponentInvlevelInList
                    component={item}
                    location_id={item.current_location_id}
                    invlevel={item.physical_invlevel}
                    onInvlevelChange={() => {
                        item.refetchData()
                    }}
                />
            </div>
        </div>
    )

    const PhysicalInvlevelTotalCell = (item) => {
        return (
            <div className="table-cell p-5 text-gray-400 text-sm">
                <ComponentInvlevelInList
                    component={item}
                    invlevel={item.physical_invlevel_total}
                    location_id="all"
                />
            </div>
        )
    }

    const LastInventoryDaysCell = (item) => {
        return (
            <div className="table-cell p-5 text-sm text-gray-400 whitespace-nowrap">
                <ComponentInventoryStatus
                    last_inventory_days={item.last_inventory_days}
                    last_inventory_date={item.last_inventory}
                    needs_inventory={
                        item.inventory_cycle_days !== null &&
                        item.physical_invlevel !== null
                    }
                />
            </div>
        )
    }

    const LastInventoryDateCell = (item) => (
        <div className="table-cell w-[300px] p-5 text-gray-400 text-sm">
            {item.last_inventory && (
                <div className="flex flex-col">
                    <span>
                        {'Il y a ' + dayjs(item.last_inventory).fromNow(true)}
                    </span>
                </div>
            )}

            {item.last_inventory === null && (
                <div className="flex flex-col">
                    <span>Jamais inventorié</span>
                </div>
            )}
        </div>
    )

    const InventoryCycleDaysCell = (item) => (
        <div className="table-cell  w-[250px] p-5 text-gray-400 text-sm">
            {item.inventory_cycle_days && (
                <div className="flex flex-col">
                    <span>{item.inventory_cycle_days} jours</span>
                </div>
            )}
        </div>
    )

    const AssemblableQuantityCell = (item, key) => (
        <div className="table-cell  w-[200px] p-5 text-gray-400 text-sm">
            {item[key] != null && (
                <div className="flex flex-col text-purple-500">
                    <span>{item[key]} assemblables</span>
                </div>
            )}

            {item[key] == null && (
                <div className="flex flex-col">
                    <span>-</span>
                </div>
            )}
        </div>
    )

    const FlowRateCell = (item) => (
        <div className="table-cell  w-[200px] p-5 text-gray-400 text-sm">
            {item.flow_rate !== null && (
                <div className="flex flex-col">
                    <span>{item.flow_rate.toFixed(2)} / J</span>
                </div>
            )}
        </div>
    )

    const DispatchDateCell = (item) => (
        <div className="table-cell w-[200px] p-5 text-gray-400 text-sm whitespace-nowrap">
            {item.dispatch_date && (
                <div className="flex flex-col">
                    <span className="font-medium text-orange-500">
                        {dayjs(item.dispatch_date).format('ll')}
                    </span>
                </div>
            )}
        </div>
    )

    const LastingDaysCell = (item) => {
        const lastingDaysClassNames = classNames({
            'text-red-500': item.lasting_days < 90,
            'text-yellow-500':
                item.lasting_days >= 90 && item.lasting_days < 180,
            'text-green-500': item.lasting_days >= 180,
        })

        return (
            <div className="table-cell p-5 w-[200px] text-gray-400 text-sm">
                {item.lasting_days !== null && (
                    <div className="flex flex-col">
                        <span className={lastingDaysClassNames}>
                            {item.lasting_days} jours
                        </span>
                    </div>
                )}
            </div>
        )
    }

    const LastingDaysWithAssemblableQuantityCell = (item) => {
        const lastingDaysClassNames = classNames({
            'text-red-500': item.lasting_days_with_assemblable_quantity < 90,
            'text-yellow-500':
                item.lasting_days_with_assemblable_quantity >= 90 &&
                item.lasting_days_with_assemblable_quantity < 180,
            'text-green-500':
                item.lasting_days_with_assemblable_quantity >= 180,
        })

        return (
            <div className="table-cell p-5 w-[200px] text-gray-400 text-sm">
                {item.lasting_days_with_assemblable_quantity !== null && (
                    <div className="flex flex-col">
                        <span className={lastingDaysClassNames}>
                            {item.lasting_days_with_assemblable_quantity} jours
                        </span>
                    </div>
                )}
            </div>
        )
    }

    const RemainingQuantityCell = (item) => (
        <div className="table-cell w-[200px] p-5 text-gray-400 text-sm">
            <div className="flex flex-col">
                <span className="font-medium text-baltic">
                    {item.remaining_quantity_sum ?? 0}
                </span>
            </div>
        </div>
    )

    const ChildrenCount = (item) => (
        <div className="table-cell p-5 text-gray-400 text-sm">
            {item.children_count > 0 && (
                <span className="text-gray-700">
                    {item.children_count} composants
                </span>
            )}

            {!item.children_count && <span>Sans assemblage</span>}
        </div>
    )

    const ProductionOrderQuantity = (item) => {
        const textClasses = classNames({
            'text-gray-900': item.production_orders_quantity > 0,
        })

        return (
            <div className="table-cell w-[200px] p-5 text-gray-400 text-sm">
                {item.children_count > 0 && (
                    <span className={textClasses}>
                        {item.production_orders_quantity ?? 0} en assemblage
                    </span>
                )}
            </div>
        )
    }

    const CreatedAtCell = (item) => {
        return (
            <div className="table-cell p-5 text-gray-400 text-sm whitespace-nowrap">
                {dayjs(item.created_at).calendar()}
            </div>
        )
    }

    const CollectionCell = (item) => {
        return (
            <div className="table-cell p-5 text-gray-400 text-sm">
                {item.collections.map((c) => c.name_fr).join(', ')}
            </div>
        )
    }

    const WarrantyVisibleByAllRetailersCell = (item) => {
        return (
            <div className="table-cell p-5 text-gray-400 text-sm">
                <Badge
                    color={
                        item.warranty_visible_by_all_retailers ? 'green' : 'red'
                    }
                >
                    {item.warranty_visible_by_all_retailers ? 'Oui' : 'Non'}
                </Badge>
            </div>
        )
    }

    const WarrantyVisibleOnWebsiteContactCell = (item) => {
        return (
            <div className="table-cell p-5 text-gray-400 text-sm">
                <Badge
                    color={
                        item.warranty_visible_on_website_contact
                            ? 'green'
                            : 'red'
                    }
                >
                    {item.warranty_visible_on_website_contact ? 'Oui' : 'Non'}
                </Badge>
            </div>
        )
    }

    const SellingB2CStatusCell = (item) => {
        const wrap = (content) => (
            <div className="table-cell p-5 text-sm whitespace-nowrap">{content}</div>
        )

        if (item.availability_b2c === 'force_disabled') {
            return wrap(<span className="text-red-500">Rupture forcée</span>)
        }

        if (item.availability_b2c === 'force_available' && !item.dispatch_date) {
            return wrap(<span className="text-green-500">Vendre en illimité</span>)
        }

        if (item.dispatch_date && (item.availability_b2c === 'force_available' || item.physical_invlevel > 0)) {
            return wrap(
                <span className="text-orange-500">
                    {'Exp. au ' + dayjs(item.dispatch_date).format('ll')}
                </span>
            )
        }

        if (item.physical_invlevel <= 0) {
            return wrap(<span className="text-red-500">Rupture de stock</span>)
        }

        if (item.availability_b2c === 'follow') {
            return wrap(<span className="text-green-500">Exp. 3 à 5 jours</span>)
        }

        return wrap(<span className="text-gray-400">Inconnu</span>)
    }

    const CoverageDaysCell = (item) => {
        const coverageMonths = Math.floor(item.coverage_days / 30)

        return (
            <div className="table-cell p-5 text-gray-400 text-sm whitespace-nowrap">
                <span>
                    {item.coverage_days
                        ? Intl.NumberFormat('fr-FR').format(coverageMonths) +
                          ' mois'
                        : '-'}
                </span>
            </div>
        )
    }

    const KeepInStockCell = (item) => {
        const isKeepInStock = item.is_keep_in_stock ?? true

        return (
            <div className="table-cell p-5 text-sm whitespace-nowrap">
                <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        isKeepInStock
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                    }`}
                >
                    {isKeepInStock ? (
                        <IconCheck size={12} stroke={2.5} />
                    ) : (
                        <IconX size={12} stroke={2} />
                    )}
                    {isKeepInStock ? 'Suivi actif' : 'Suivi désactivé'}
                </span>
            </div>
        )
    }

    const FulfillableInventoryCell = (item) => (
        <div className="table-cell p-5 text-gray-400 text-sm">
            <span
                className={
                    item.fulfillable_inventory > 0
                        ? 'text-green-600'
                        : 'text-red-500'
                }
            >
                {item.fulfillable_inventory ?? 0}
            </span>
        </div>
    )

    const HoldQuantityCell = (item) => (
        <div className="table-cell p-5 text-gray-400 text-sm">
            {item.hold_quantity > 0 ? (
                <span className="text-indigo-600">{item.hold_quantity}</span>
            ) : (
                <span className="text-gray-400">-</span>
            )}
        </div>
    )

    const AvailableQuantityCell = (item) => {
        const available =
            (item.fulfillable_inventory ?? 0) - (item.hold_quantity ?? 0)

        return (
            <div className="table-cell p-5 text-sm">
                <span
                    className={`font-medium ${
                        available > 0 ? 'text-green-600' : 'text-red-500'
                    }`}
                >
                    {available}
                </span>
            </div>
        )
    }

    const AvailableLastingDaysCell = (item) => {
        const available =
            (item.fulfillable_inventory ?? 0) - (item.hold_quantity ?? 0)
        const flowRate = item.flow_rate

        if (available <= 0) {
            return (
                <div className="table-cell p-5 text-sm">
                    <span className="text-red-500">Rupture</span>
                </div>
            )
        }

        if (!flowRate || flowRate <= 0) {
            return <div className="table-cell p-5 text-sm text-gray-400">-</div>
        }

        const lastingDays = Math.floor(available / flowRate)

        // Check if next assembly comes after stock runs out
        const nextAssemblyEndingAt = item.next_production_order_ending_at
        let isCritical = false
        if (nextAssemblyEndingAt) {
            const daysUntilAssembly = dayjs(nextAssemblyEndingAt).diff(
                dayjs(),
                'day'
            )
            isCritical = daysUntilAssembly > lastingDays
        }

        return (
            <div className="table-cell p-5 text-sm">
                <span className={isCritical ? 'text-red-500' : 'text-gray-500'}>
                    {lastingDays} jours
                </span>
            </div>
        )
    }

    const ValueCell = (item) => {
        if (!item.value) {
            return (
                <div className="table-cell p-5 text-sm text-gray-400">
                    <span className="text-orange-500">Non définie</span>
                </div>
            )
        }

        const currency = item.value_currency || 'EUR'
        const formatted = new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: currency,
        }).format(item.value)

        return (
            <div className="table-cell p-5 text-sm text-gray-600">
                {formatted}
            </div>
        )
    }

    const NextPositiveStockMovementCell = (item) => {
        const prodDate = item.next_production_order_ending_at
            ? dayjs(item.next_production_order_ending_at)
            : null
        const purchDate = item.next_purchase_order_reception_at
            ? dayjs(item.next_purchase_order_reception_at)
            : null

        // Determine which comes first
        let isProd = false
        if (prodDate && purchDate) {
            isProd = prodDate.isBefore(purchDate)
        } else {
            isProd = !!prodDate
        }

        const date = isProd ? prodDate : purchDate
        if (!date) {
            return <div className="table-cell p-5 text-sm text-gray-400">-</div>
        }

        const quantity = isProd
            ? Math.max(
                  0,
                  item.next_production_order_quantity -
                      (item.next_production_order_quantity_completed ?? 0)
              )
            : (item.next_purchase_order_quantity ?? 0)

        const href = isProd
            ? `/assembly/${item.next_production_order_id}`
            : `/purchase_orders/${item.next_purchase_order_id}`

        const Icon = isProd ? IconClockCog : IconTransferIn
        const color = isProd ? 'text-purple-600' : 'text-pink-600'

        const daysUntil = date.diff(dayjs(), 'day')

        return (
            <Link to={href} className="table-cell p-5 text-sm hover:bg-gray-50">
                <div className="flex items-center justify-end gap-2">
                    <span
                        className={
                            daysUntil < 0 ? 'text-red-500' : 'text-gray-600'
                        }
                    >
                        {daysUntil < 0
                            ? 'Retard'
                            : daysUntil === 0
                              ? "Aujourd'hui"
                              : `${daysUntil}j`}
                    </span>
                    <span className="text-gray-400">→</span>
                    <Icon size={16} className={color} />
                    <span className={`${color} font-medium`}>+{quantity}</span>
                </div>
            </Link>
        )
    }

    // Columns
    const availableColumns = [
        {
            name: 'SKU',
            key: 'id',
            display: 'optional',
            sortable: true,
            component: SKUCell,
        },
        {
            name: 'Composant',
            key: 'name',
            display: 'mandatory',
            sortable: true,
            component: NameCall,
            width: 320,
        },
        {
            name: 'Stock local',
            key: 'physical_invlevel',
            display: 'optional_default',
            sortable: true,
            component: StockCell,
            tooltip: "Stock présent dans le lieu sélectionné.",
            width: 190,
        },
        {
            name: 'Stock global',
            key: 'physical_invlevel_total',
            display: 'optional',
            sortable: true,
            tooltip: 'Stock présent dans tous les lieux.',
            component: PhysicalInvlevelTotalCell,
            width: 190,
        },
        {
            name: 'Inventaire',
            key: 'last_inventory_days',
            display: 'optional',
            sortable: true,
            tooltip: "Retard par rapport au cycle d'inventaire, à l'emplacement sélectionné. Positif = en retard, négatif = encore dans les délais.",
            formula: "Jours depuis le dernier inventaire (emplacement sélectionné) − cycle d'inventaire",
            component: LastInventoryDaysCell,
            width: 190,
        },
        {
            name: 'Dernier inventaire',
            key: 'last_inventory',
            display: 'optional',
            sortable: true,
            tooltip: 'Dernier inventaire dans le lieu sélectionné.',
            component: LastInventoryDateCell,
        },
        {
            name: "Cycle d'inventaire",
            key: 'inventory_cycle_days',
            display: 'optional',
            sortable: true,
            tooltip: 'Nombre de jours prévu entre deux inventaires.',
            component: InventoryCycleDaysCell,
        },
        {
            name: 'Écoulement',
            key: 'flow_rate',
            display: 'optional',
            sortable: true,
            tooltip: "Moyenne des unités expédiées par jour sur 90 jours, hors transferts. Peut être remplacée par une valeur manuelle.",
            formula: "Σ unités expédiées sur 90 jours (hors transferts) ÷ 90",
            component: FlowRateCell,
        },
        {
            name: 'Jours restants en stock',
            key: 'lasting_days',
            display: 'optional',
            sortable: true,
            tooltip: "Jours avant rupture à MNP, Paris et Perfex réunis. Inclut les préparations et assemblages prévus, hors transferts internes et réceptions fournisseurs. Maximum : 1 000 jours.",
            component: LastingDaysCell,
        },
        {
            name: 'JRA',
            key: 'assembly_lasting_days',
            display: 'optional',
            sortable: true,
            tooltip: "Jours de stock en assemblant les pièces disponibles dans le lieu sélectionné.",
            formula: "(Stock local + quantité assemblable) ÷ écoulement",
            component: LastingDaysWithAssemblableQuantityCell,
        },
        {
            name: 'JRA tous emplacements',
            key: 'assembly_lasting_days_total',
            display: 'optional',
            sortable: true,
            tooltip: "Jours de stock en regroupant et assemblant les pièces de tous les lieux. Réservations non déduites ; les pièces partagées sont comptées pour chaque produit.",
            formula: "(Stock physique tous lieux + assemblable tous lieux) ÷ écoulement",
            component: (item) => LastingDaysWithAssemblableQuantityCell({
                ...item,
                lasting_days_with_assemblable_quantity: item.lasting_days_with_assemblable_quantity_total,
            }),
        },
        ...[
            {
                key: 'assemblable_quantity_total',
                name: 'Assemblable — tous emplacements',
                tooltip: "Quantité assemblable en mélangeant les pièces de tous les lieux. Réservations non déduites ; les pièces partagées sont comptées pour chaque produit.",
                formula: "Minimum, pour chaque pièce : ⌊stock physique cumulé tous lieux ÷ quantité nécessaire par unité⌋",
            },
            {
                key: 'assemblable_quantity_mnp',
                name: 'Assemblable — MNP',
                tooltip: "Quantité assemblable avec les pièces à MNP. Réservations non déduites ; les pièces partagées sont comptées pour chaque produit.",
                formula: "Minimum, pour chaque pièce : ⌊stock physique MNP ÷ quantité nécessaire par unité⌋",
            },
            {
                key: 'assemblable_quantity_paris',
                name: 'Assemblable — Atelier Paris',
                tooltip: "Quantité assemblable avec les pièces à l'Atelier Paris. Réservations non déduites ; les pièces partagées sont comptées pour chaque produit.",
                formula: "Minimum, pour chaque pièce : ⌊stock physique Atelier Paris ÷ quantité nécessaire par unité⌋",
            },
            {
                key: 'assemblable_quantity_perfex',
                name: 'Assemblable — Perfex',
                tooltip: "Quantité assemblable avec les pièces à Perfex. Réservations non déduites ; les pièces partagées sont comptées pour chaque produit.",
                formula: "Minimum, pour chaque pièce : ⌊stock physique Perfex ÷ quantité nécessaire par unité⌋",
            },
        ].map((column) => ({
            ...column,
            replaces: 'assemblable_quantity',
            display: 'optional',
            sortable: true,
            component: (item) => AssemblableQuantityCell(item, column.key),
        })),
        {
            name: 'Date de préparation',
            key: 'dispatch_date',
            display: 'optional',
            sortable: true,
            tooltip: "Date de disponibilité annoncée pour les précommandes Shopify.",
            component: DispatchDateCell,
        },
        {
            name: "Nombre d'éléments",
            key: 'children_count',
            display: 'optional',
            sortable: false,
            component: ChildrenCount,
        },
        {
            name: 'Quantité à préparer',
            key: 'remaining_quantity_sum',
            display: 'optional',
            sortable: true,
            tooltip: "Quantité restante à expédier dans les expéditions ouvertes à l'emplacement sélectionné. Commandes payées uniquement, hors showroom et risquées.",
            formula: "Σ quantité restante (expéditions ouvertes, emplacement sélectionné)",
            component: RemainingQuantityCell,
        },
        {
            name: 'Quantité en assemblage',
            key: 'production_orders_quantity',
            display: 'optional',
            sortable: true,
            tooltip: "Quantité encore à assembler, tous lieux confondus. Inclut les assemblages en cours et programmés.",
            formula: "Σ (quantité − déjà terminé) sur tous les assemblages ouverts/programmés (global)",
            component: ProductionOrderQuantity,
        },
        {
            name: 'Date de création',
            key: 'created_at',
            display: 'optional',
            sortable: true,
            component: CreatedAtCell,
        },
        {
            name: 'Collections',
            key: 'collections',
            display: 'optional',
            sortable: false,
            component: CollectionCell,
        },
        {
            name: 'Associable par les revendeurs',
            key: 'warranty_visible_by_all_retailers',
            display: 'optional',
            sortable: false,
            tooltip: "Permet aux revendeurs d'associer ce composant à une montre.",
            component: WarrantyVisibleByAllRetailersCell,
        },
        {
            name: 'Visible sur le formulaire de contact',
            key: 'warranty_visible_on_website_contact',
            display: 'optional',
            sortable: false,
            tooltip: "Affiche ce composant dans le formulaire SAV du site.",
            component: WarrantyVisibleOnWebsiteContactCell,
        },
        {
            name: 'Vente B2C',
            key: 'selling_b2c_status',
            display: 'optional',
            sortable: false,
            width: 170,
            tooltip: 'Disponibilité annoncée sur le site, selon le stock, la date de préparation et les réglages de vente.',
            component: SellingB2CStatusCell,
        },
        {
            name: 'Couverture',
            key: 'coverage_days',
            display: 'optional',
            sortable: true,
            component: CoverageDaysCell,
            tooltip: "Nombre de mois de stock restants. Pour un composant simple : calculé sur le stock global. Pour un bundle : minimum de couverture parmi les composants enfants.",
            formula: "(Stock global − réservations globales + commandes fournisseurs ouvertes) ÷ écoulement (global)",
            width: 190,
        },
        {
            name: 'Suivi stock',
            key: 'is_keep_in_stock',
            display: 'optional',
            sortable: true,
            component: KeepInStockCell,
            tooltip: 'Indique si ce composant doit être surveillé et réapprovisionné.',
            width: 130,
        },
        {
            name: 'Quantité MNP + Atelier',
            key: 'fulfillable_inventory',
            display: 'optional',
            sortable: true,
            component: FulfillableInventoryCell,
            tooltip: "Stock physique dans les emplacements autorisés à expédier des commandes (MNP + Atelier uniquement).",
            formula: "Stock physique MNP + stock physique Atelier",
            width: 150,
        },
        {
            name: 'Quantité réservé',
            key: 'hold_quantity',
            display: 'optional',
            sortable: true,
            component: HoldQuantityCell,
            tooltip: "Unités réservées pour les préparations, les devis revendeurs et les pièces des assemblages en cours ou programmés, tous lieux confondus.",
            formula: "Σ réservations (tous emplacements)",
            width: 150,
        },
        {
            name: 'Quantité disponible',
            key: 'available_quantity',
            display: 'optional',
            sortable: true,
            component: AvailableQuantityCell,
            tooltip: "Ce qui peut encore être vendu ou affecté à une commande.",
            formula: "Stock physique MNP + stock physique Atelier − quantité réservée (tous emplacements)",
            width: 150,
        },
        {
            name: 'Jours restants dispo',
            key: 'available_lasting_days',
            display: 'optional',
            sortable: true,
            component: AvailableLastingDaysCell,
            tooltip: "Jours de stock après déduction des réservations.",
            formula: "Quantité disponible ÷ écoulement (global)",
            width: 150,
        },
        {
            name: 'Prochain mvt stock +',
            key: 'next_positive_stock_movement',
            display: 'optional',
            sortable: false,
            component: NextPositiveStockMovementCell,
            tooltip: "Première date prévue de fin d'assemblage ou de réception fournisseur, tous lieux confondus.",
            width: 180,
        },
        {
            name: "Valeur d'achat",
            key: 'value',
            display: 'optional',
            sortable: true,
            component: ValueCell,
            tooltip: "Prix d'achat unitaire utilisé pour la douane et l'export d'inventaire.",
            width: 130,
        },
    ]

    // Filters
    const { data: categories } = useApiSWRReference('/component-categories')

    const categoryOptions =
        categories?.map((c) => ({
            value: c.slug,
            label: c.name_fr,
        })) ?? []

    const availableFilters = [
        {
            name: 'Catégorie',
            key: 'category',
            type: 'multiselect',
            placeholder: 'Cadran, boitier, tête de montre, bracelet...',
            options: categoryOptions,
        },
        {
            name: 'Stockage',
            key: 'physical_inventory',
            type: 'select',
            placeholder: 'Stockés uniquement, non stockés uniquement',
            options: [
                { label: "Stockés à l'emplacement", value: 'with' },
                { label: 'Non stockés', value: 'without' },
            ],
        },
        {
            name: "Cycle d'inventaire",
            key: 'inventory_cycle_days',
            type: 'select',
            placeholder: "Avec cycle d'inventaire, sans cycle d'inventaire",
            options: [
                { label: 'Défini', value: 'with' },
                { label: 'Non défini', value: 'without' },
            ],
        },
        {
            name: 'Quantité à préparer',
            key: 'remaining_quantity',
            type: 'select',
            options: [
                { value: 'with', label: 'Avec' },
                { value: 'without', label: 'Sans' },
            ],
        },
        {
            name: 'Composants en vente',
            key: 'is_permanently_sold',
            type: 'select',
            options: [
                { value: 'yes', label: 'Oui' },
                { value: 'no', label: 'Non' },
            ],
        },
        {
            name: 'Statut',
            key: 'is_archived',
            type: 'select',
            options: [
                { value: 'true', label: 'Archivés' },
                { value: 'false', label: 'Non archivés' },
            ],
        },
        {
            name: 'Suivi stock',
            key: 'is_keep_in_stock',
            type: 'select',
            options: [
                { value: 'true', label: 'Suivi actif' },
                { value: 'false', label: 'Suivi désactivé' },
            ],
        },
        {
            name: "Valeur d'achat",
            key: 'has_value',
            type: 'select',
            placeholder: 'Avec ou sans valeur définie',
            options: [
                { value: 'with', label: 'Définie' },
                { value: 'without', label: 'Non définie' },
            ],
        },
    ]

    return (
        <Table
            engineApiEndpoint="components"
            availableColumns={availableColumns}
            availableFilters={availableFilters}
            searchable={true}
            withLocation={true}
            defaultSortColumn="name"
            defaultSortDirection="asc"
        />
    )
}
